import React, { PureComponent } from "react";

import {
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  Legend,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList,
} from "recharts";

import {
  Metric,
  StandardTooltip,
  CustomizedXAxisTickTimeDay,
  CustomizedYAxisTick,
  CustomizedYAxisTickRight,
  CustomizedYAxisTickRightCurrency,
  selectFormat,
  Error,
  formatDateFromData,
  MAIN_COLORS,
  getAllKeys,
} from "components/Utils/Utils.js";

import {
  fetchInsights,
  RenderTooltipInsights,
  OpenSelectedInsight,
  CloseSelectedInsight,
  InsightsWithinTooltip,
  InsightsLineLabel,
} from "components/Utils/InsightsUtils.js";

import { XRayTooltip } from "components/Utils/XRayTooltip/XRayTooltip.js";

import { getXrayTooltipPosition } from "helpers/DOM";

class GothamDualAxis extends PureComponent {
  constructor(props) {
    super(props);
    this.node = React.createRef();
    this.state = {
      widgetsData: props.data.data,
      widgetsInsights: [],
      width: 0,
      height: 0,
      tooltip: {
        show: false,
        x: 0,
        y: 0,
      },
    };

    this.divPosition = React.createRef();
    this.updatePosition = this.updatePosition.bind(this);
  }

  updatePosition() {
    this.closeTooltip();

    if (!this.chartRef || !this.chartRef.state) return;

    const containerWidth = this.chartRef.state.containerWidth || 0;
    const containerHeight = this.chartRef.state.containerHeight || 0;
    this.setState({ width: containerWidth, height: containerHeight });
  }

  UNSAFE_componentWillMount() {
    this.updatePosition();
  }

  componentDidMount() {
    this.props.childRef(this);

    window.addEventListener("resize", this.updatePosition);
    window.addEventListener("click", this.handleClickOutside);

    setTimeout(() => {
      this.updatePosition();
    }, 500);

    // Fetch insights
    const { data, project_id, project, insights, insightsConfig } = this.props;

    if (insights) {
      const insightsPromise = fetchInsights(
        data,
        project,
        project_id,
        insightsConfig
      );

      if (insightsPromise !== undefined) {
        insightsPromise
          .then((data) => {
            if (data && data.length > 0) {
              this.setState({
                widgetsInsights: data,
              });
            }
          })
          .catch((error) => {
            console.log(error);
          });
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.updatePosition);
    window.removeEventListener("click", this.handleClickOutside);
  }

  closeTooltip = () => {
    this.setState({
      tooltip: { show: false },
    });
  };

  handleClickOutside = (e) => {
    if (
      this.divPosition.current &&
      this.divPosition.current.contains(e.target)
    ) {
      return;
    } else {
      this.closeTooltip();
    }
  };

  detectDataKeys = (data) => {
    if (!data || !data[0]) return { primary: null, secondary: null };

    const sampleItem = data[0];
    const keys = Object.keys(sampleItem).filter((key) => key !== "date");

    // Determine which metrics are present in the data
    const hasImpressions = "impressions" in sampleItem;
    const hasClicks = "clicks" in sampleItem;
    const hasCpm = "cpm" in sampleItem;
    const hasCpc = "cpc" in sampleItem;

    // Define the primary and secondary metrics based on what's available
    if (hasImpressions && hasCpm) {
      return {
        primary: "impressions",
        secondary: "cpm",
        primaryLabel: "Impressions",
        secondaryLabel: "CPM",
        secondaryFormat: "currency",
      };
    } else if (hasClicks && hasCpc) {
      return {
        primary: "clicks",
        secondary: "cpc",
        primaryLabel: "Clicks",
        secondaryLabel: "CPC",
        secondaryFormat: "currency",
      };
    }

    // Fallback to the first two non-date keys if the expected pairs aren't found
    if (keys.length >= 2) {
      return {
        primary: keys[0],
        secondary: keys[1],
        primaryLabel: keys[0].toUpperCase(),
        secondaryLabel: keys[1].toUpperCase(),
        secondaryFormat:
          keys[1] === "cpc" || keys[1] === "cpm" ? "currency" : "number",
      };
    }

    return { primary: null, secondary: null };
  };

  getFormatType = (key) => {
    // Return appropriate format type based on the key name
    if (key === "cpc" || key === "cpm") {
      return "currency";
    }
    return "number";
  };

  getSortedKeys(_data) {
    if (!_data) return [];
    if (!_data[0]) return [];
    const data = _data.filter((d) => d);
    const keys = getAllKeys(data, ["date", "topContent", "total", "full_date"]);

    const len = data.length;

    return keys
      .map((key) => ({
        key: key,
        value: data.reduce((accum, curr) => accum + (curr[key] || 0), 0) / len,
      }))
      .sort((a, b) => b.value - a.value)
      .map((d) => d.key);
  }

  getExportData = () => {
    const { data } = this.props;
    const { primary, secondary } = this.detectDataKeys(data.graph);

    return {
      data: data.graph.map((item) => ({
        date: item.date,
        [primary]: item[primary],
        [secondary]: item[secondary],
      })),
      opts: {
        type: "other",
        date: {
          defined: true,
          format: "YYYY-MM-DDThh:mm:ssZ",
          viewFormat: "YYYY-MM-DD",
        },
      },
    };
  };

  render() {
    const { data, colorIndex = 0 } = this.props;
    const { widgetsInsights } = this.state;

    // Create a color scheme based on MAIN_COLORS
    const colors = [
      MAIN_COLORS[colorIndex % MAIN_COLORS.length], // area fill/stroke
      MAIN_COLORS[(colorIndex + 1) % MAIN_COLORS.length], // gradient
      MAIN_COLORS[(colorIndex + 2) % MAIN_COLORS.length], // line
    ];

    const widget_data = data.graph;
    const insightsData = widgetsInsights.length > 0;

    if (data.hasData === false) {
      return <Error minHeight={360} />;
    }

    const {
      primary,
      secondary,
      primaryLabel,
      secondaryLabel,
      secondaryFormat = "number",
    } = this.detectDataKeys(widget_data);

    if (!primary || !secondary) {
      return <Error minHeight={360} message="Invalid data format" />;
    }

    let preparedData = formatDateFromData(widget_data);

    return (
      <>
        <div key={"stats"} className="row">
          {data.data[0][primary] && (
            <div className="col-6 col-md-3 mb-3">
              <h6 className="widget__metric-label">{primaryLabel}</h6>
              <Metric {...data.data[0][primary]} size={"sm"} />
            </div>
          )}

          {data.data[0][secondary] && (
            <div className="col-6 col-md-3 mb-3">
              <h6 className="widget__metric-label">{secondaryLabel}</h6>
              <Metric
                {...data.data[0][secondary]}
                size={"sm"}
                type={secondaryFormat}
              />
            </div>
          )}

          {data.data[0].total_spend && (
            <div className="col-6 col-md-3 mb-3">
              <h6 className="widget__metric-label">Total Spend</h6>
              <Metric
                {...data.data[0].total_spend}
                size={"sm"}
                type="currency"
              />
            </div>
          )}
        </div>

        <div className="row" ref={this.divPosition}>
          <ResponsiveContainer
            className="legends-non-interactive"
            height={250}
            width="100%"
            key={"graph"}
            ref={(ref) => (this.chartRef = ref)}
          >
            <ComposedChart
              data={preparedData}
              onMouseOver={(e) => CloseSelectedInsight(this.node)}
              onMouseOut={(e) =>
                OpenSelectedInsight(this.node, !this.state.tooltip.show)
              }
              isAnimationActive={false}
              onClick={(d) => {
                if (d === null) {
                  this.closeTooltip();
                  return;
                }

                // Find the actual data point from the preparedData using the activeLabel
                const activeLabel = d.activeLabel;
                console.log("Active Label:", activeLabel);
                console.log("Prepared data:", preparedData);

                // Find the data point that matches the active label in the prepared data
                const dataPoint = preparedData.find(
                  (point) => point.date === activeLabel
                );
                console.log("Found data point:", dataPoint);

                if (!dataPoint) {
                  console.error(
                    "No data point found for active label:",
                    activeLabel
                  );
                  return;
                }

                // Get values directly from the actual data point
                let primaryValue = Number(dataPoint[primary]);
                primaryValue = isNaN(primaryValue) ? 0 : primaryValue;

                let secondaryValue = Number(dataPoint[secondary]);
                secondaryValue = isNaN(secondaryValue) ? 0 : secondaryValue;

                console.log(
                  "Using values from data point - Primary:",
                  primaryValue,
                  "Secondary:",
                  secondaryValue
                );

                // Add some additional metric data for better tooltip display
                const formattedPayload = {
                  ...dataPoint,
                  // Add formatted values for display
                  [`${primary}_formatted`]: selectFormat(
                    "number",
                    primaryValue
                  ),
                  [`${secondary}_formatted`]: selectFormat(
                    secondaryFormat,
                    secondaryValue
                  ),
                };

                // Create the extra data object for XRayLineChartTooltip
                // It needs to be indexable by the metric name (used in activePayload[key].name)
                const extra = {
                  [primary]: {
                    label: primaryLabel,
                    color: colors[0],
                    value: primaryValue,
                    format: "number",
                    formatType: "number",
                    formattedValue: selectFormat("number", primaryValue),
                  },
                  [secondary]: {
                    label: secondaryLabel,
                    color: colors[2],
                    value: secondaryValue,
                    format: secondaryFormat,
                    formatType: secondaryFormat,
                    formattedValue: selectFormat(
                      secondaryFormat,
                      secondaryValue
                    ),
                  },
                };

                // XRayLineChartTooltip expects an object with numeric keys, not an array
                const activePayload = {
                  0: {
                    name: secondary,
                    dataKey: secondary,
                    value: secondaryValue,
                    color: colors[2],
                    fill: colors[2],
                    stroke: colors[2],
                    strokeWidth: 2,
                    payload: formattedPayload,
                  },
                  1: {
                    name: primary,
                    dataKey: primary,
                    value: primaryValue,
                    color: colors[0],
                    fill: colors[0],
                    stroke: colors[0],
                    strokeWidth: 2,
                    payload: formattedPayload,
                  },
                };

                this.setState(
                  {
                    tooltip: {
                      ...d,
                      payload: formattedPayload,
                      activePayload,
                      extra,
                      show: true,
                      indicator: "metrics", // This should be correct for XRayLineChartTooltip
                      activeLabel: dataPoint.date,
                      date: dataPoint.date,
                      // Add values directly to the tooltip for failsafe access
                      primary: primaryValue,
                      secondary: secondaryValue,
                      primaryLabel,
                      secondaryLabel,
                    },
                  },
                  () => {
                    // Debug logging
                    console.log("Tooltip Data:", this.state.tooltip);
                    console.log(
                      "ActivePayload:",
                      this.state.tooltip.activePayload
                    );
                    console.log("Extra:", this.state.tooltip.extra);
                    console.log("Primary Value:", primaryValue);
                    console.log("Secondary Value:", secondaryValue);
                  }
                );
              }}
            >
              <defs>
                <linearGradient
                  id="GRADIENT-PRIMARY"
                  x1="0"
                  y1="1"
                  x2="0"
                  y2="0"
                >
                  <stop offset="5%" stopColor={colors[0]} stopOpacity={1} />
                  <stop offset="95%" stopColor={colors[1]} stopOpacity={1} />
                </linearGradient>
              </defs>

              <CartesianGrid
                stroke="#FEE5DE"
                strokeDasharray="3 3"
                vertical={false}
              />

              <Area
                yAxisId="left"
                scale="auto"
                dataKey={primary}
                type="monotone"
                fillOpacity={0.2}
                fill="url(#GRADIENT-PRIMARY)"
                stroke={colors[0]}
                isAnimationActive={false}
                name={primaryLabel}
              >
                {insightsData && (
                  <LabelList
                    dataKey="date"
                    content={
                      <InsightsLineLabel
                        height={180}
                        data={preparedData}
                        insights={this.state.widgetsInsights}
                        showIndicator={!this.state.tooltip.show}
                        node={this.node}
                      />
                    }
                  />
                )}
              </Area>

              <YAxis
                dataKey={primary}
                interval={"preserveEnd"}
                yAxisId="left"
                orientation="left"
                axisLine={false}
                tickLine={false}
                mirror={true}
                tick={<CustomizedYAxisTick />}
              />

              <YAxis
                dataKey={secondary}
                interval={"preserveEnd"}
                yAxisId="right"
                orientation="right"
                axisLine={false}
                tickLine={false}
                mirror={true}
                stroke={colors[2]}
                tick={
                  secondary === "cpc" || secondary === "cpm" ? (
                    <CustomizedYAxisTickRightCurrency />
                  ) : (
                    <CustomizedYAxisTickRight />
                  )
                }
              />

              <XAxis
                dataKey="date"
                interval={"preserveStartEnd"}
                scale="point"
                axisLine={false}
                tickLine={false}
                tick={<CustomizedXAxisTickTimeDay />}
                stroke={colors[2]}
              />
              <Legend
                verticalAlign="bottom"
                align="center"
                iconType="square"
                iconSize={15}
                height={35}
                wrapperStyle={{ bottom: -10 }}
              />
              <Line
                yAxisId="right"
                scale="auto"
                dataKey={secondary}
                type="monotone"
                stroke={colors[2]}
                strokeWidth="2"
                dot={false}
                name={secondaryLabel}
              />
              {!this.state.tooltip.show && (
                <Tooltip
                  cursor={{ fill: "#eee", opacity: 0.5 }}
                  content={
                    <StandardTooltip
                      render={({ data }) => {
                        const payload = data(this.props);

                        if (payload !== null) {
                          const tooltip_data = payload[0].payload;

                          return (
                            <div className="standard-tooltip">
                              {tooltip_data && tooltip_data.date && (
                                <header className="standard-tooltip__header">
                                  <span className="standard-tooltip__key-header">
                                    Date:
                                  </span>
                                  <span className="standard-tooltip__value-header">
                                    {tooltip_data.date}
                                  </span>
                                </header>
                              )}
                              <main className="standard-tooltip__main">
                                {tooltip_data && tooltip_data[primary] && (
                                  <div className="standard-tooltip__item">
                                    <span className="standard-tooltip__key">
                                      {payload[1] && (
                                        <span
                                          className="standard-tooltip__icon"
                                          style={{
                                            background: payload[1].stroke,
                                          }}
                                        ></span>
                                      )}
                                      {primaryLabel}
                                    </span>
                                    <span className="standard-tooltip__value">
                                      {selectFormat(
                                        "number",
                                        tooltip_data[primary]
                                      )}
                                    </span>
                                  </div>
                                )}

                                {tooltip_data && tooltip_data[secondary] && (
                                  <div className="standard-tooltip__item">
                                    <span className="standard-tooltip__key">
                                      {payload[0] && (
                                        <span
                                          className="standard-tooltip__icon"
                                          style={{
                                            background: payload[0].stroke,
                                            opacity: 0.8,
                                          }}
                                        ></span>
                                      )}
                                      {secondaryLabel}
                                    </span>
                                    <span className="standard-tooltip__value">
                                      {selectFormat(
                                        secondaryFormat,
                                        tooltip_data[secondary]
                                      )}
                                    </span>
                                  </div>
                                )}

                                {insightsData && (
                                  <InsightsWithinTooltip
                                    data={widgetsInsights}
                                    tooltipDate={tooltip_data.date}
                                  />
                                )}
                              </main>
                            </div>
                          );
                        }
                      }}
                    />
                  }
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>

          {this.state.tooltip.activeCoordinate ? (
            <div
              className="xray-tooltip-container"
              style={{
                top: "130px",
                left:
                  this.state.tooltip.activeCoordinate.x + 1062 + 220 <
                  this.state.width
                    ? this.state.tooltip.activeCoordinate.x + 15 + "px"
                    : this.state.tooltip.activeCoordinate.x -
                      getXrayTooltipPosition(
                        this.divPosition.current,
                        this.state.tooltip.activeCoordinate
                      ) +
                      "px",
                opacity: this.state.tooltip.show ? 1.0 : 0.0,
                visibility: this.state.tooltip.show ? "visible" : "hidden",
              }}
            >
              <XRayTooltip
                data={this.state.tooltip}
                insights={widgetsInsights}
                close={this.closeTooltip.bind(this)}
              />
            </div>
          ) : null}

          <div className="featured-insight-tooltip" ref={this.node}>
            {insightsData && !this.state.tooltip.show && (
              <RenderTooltipInsights data={widgetsInsights} />
            )}
          </div>
        </div>
      </>
    );
  }
}

export default GothamDualAxis;
